/**
 * SPA configuration and shared constants.
 *
 * Centralizes the API base URL resolution and the validation ranges the forms
 * mirror from the server's authoritative rules. The numeric bounds and messages
 * intentionally match `@grain/core`'s validation so inline client-side checks
 * agree with the server (the server remains authoritative — Req 2.6, 4.6, 6.6);
 * they are re-declared locally rather than imported so the browser bundle pulls
 * in no backend/Node code (e.g. argon2), per the frontend build boundary.
 */

/** The two crops the Tracker supports. */
export const CROPS = ["corn", "soybeans"] as const;
export type Crop = (typeof CROPS)[number];

/** The two price-target types. */
export const TARGET_TYPES = ["breakeven", "profit"] as const;
export type TargetType = (typeof TARGET_TYPES)[number];

/**
 * Resolve the API base URL. Prefers a deploy-time-injected
 * `window.__GRAIN_CONFIG__.apiBaseUrl`, else same-origin (empty prefix), so all
 * requests target `/api/...`.
 */
export function apiBaseUrl(): string {
  const configured = window.__GRAIN_CONFIG__?.apiBaseUrl;
  if (typeof configured === "string" && configured.length > 0) {
    return configured.replace(/\/+$/, "");
  }
  return "";
}

/** Inclusive validation bounds mirrored from the server (design: Validation Rules). */
export const RANGES = {
  actualCosts: { min: 0.01, max: 999_999_999.99, label: "Actual Costs" },
  expectedYield: { min: 0.01, max: 999_999.99, label: "Expected Yield" },
  plantedAcres: { min: 0.01, max: 999_999.99, label: "Planted Acres" },
  cashBid: { min: 0.01, max: 99_999.99, label: "Cash Bid" },
  priceTarget: { min: 0.01, max: 999_999.99, label: "Price Target" },
} as const;

/** A human label for a crop. */
export function cropLabel(crop: Crop): string {
  return crop === "corn" ? "Corn" : "Soybeans";
}

/** A human label for a target type. */
export function targetTypeLabel(type: TargetType): string {
  return type === "breakeven" ? "Breakeven" : "Desired Profit";
}
